import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, MessageCircle, Send } from 'lucide-react';
import { Button } from '../ui/Button';
import type { ApiError } from '../../lib/api';
import {
  resumirAporte,
  sesionApi,
  sesionKeys,
  type ActividadDetalle,
} from '../../lib/sesion';

/**
 * PANEL «RESPONDER» (Mago de Oz, Oleada 4) — el facilitador-mago simula el
 * sistema respondiendo en vivo a los mensajes de los participantes.
 *
 * Cada respuesta es curaduría citable (ADR-0006): un aporte `conexion` con
 * accion=responder, sobre=[el aporte respondido] y valor=el texto. El diálogo
 * queda turno a turno en el event log; el Bolsillo del autor lo ve por
 * ?mios=true. Se declara por manifiesto: conduccion.panel = "responder".
 */
export function RespuestaEnVivo({
  actividad,
  puedeConducir,
}: {
  actividad: ActividadDetalle;
  puedeConducir: boolean;
}) {
  const qc = useQueryClient();
  const [aporteId, setAporteId] = useState('');
  const [texto, setTexto] = useState('');

  const enCurso = actividad.estado === 'abierta' || actividad.estado === 'pausada';

  const aportes = useQuery({
    queryKey: ['aportes', actividad.id, 'panel-responder'],
    queryFn: () => sesionApi.aportes(actividad.id),
    enabled: enCurso && puedeConducir,
    refetchInterval: 5000,
  });

  const mut = useMutation({
    mutationFn: () =>
      sesionApi.postAporte(actividad.id, {
        tipo: 'conexion',
        payload: { accion: 'responder', sobre: [aporteId], valor: texto.trim() },
      }),
    onSuccess: () => {
      setTexto('');
      setAporteId('');
      void qc.invalidateQueries({ queryKey: ['aportes', actividad.id] });
      void qc.invalidateQueries({ queryKey: sesionKeys.aportes(actividad.id) });
    },
  });

  if (!enCurso || !puedeConducir) return null;

  // Mensajes respondibles: capturas de participantes (la curaduría no se
  // responde a sí misma). Los ya respondidos se marcan para no duplicar.
  const lista = aportes.data?.aportes ?? [];
  const respondidos = new Set(
    lista
      .filter((a) => a.tipo === 'conexion' && a.payload.accion === 'responder')
      .flatMap((a) => (Array.isArray(a.payload.sobre) ? a.payload.sobre.map(String) : [])),
  );
  const mensajes = lista.filter(
    (a) => !(a.tipo === 'conexion' && (a.payload.curaduria || a.payload.accion)),
  );

  const error = (mut.error as ApiError | null)?.detail ?? null;
  const listo = aporteId && texto.trim().length > 0;

  return (
    <section className="rounded-md border border-border-strong bg-surface-raised px-4 py-3">
      <p className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content">
        <MessageCircle className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        Responder en vivo
      </p>
      <p className="mt-1 font-sans text-xs text-content-muted">
        Vos sos el sistema: cada respuesta llega al Bolsillo de quien escribió y
        queda registrada turno a turno en el acta.
      </p>

      <div className="mt-2 flex flex-col gap-2">
        <select
          value={aporteId}
          onChange={(e) => setAporteId(e.target.value)}
          aria-label="Mensaje a responder"
          className="min-h-touch w-full rounded-md border border-border-strong bg-surface-sunken px-3 font-sans text-sm text-content focus-visible:outline-none focus-visible:shadow-focus"
        >
          <option value="" disabled>
            Elegí el mensaje a responder…
          </option>
          {mensajes.map((a) => (
            <option key={a.id} value={a.id}>
              {respondidos.has(a.id) ? '✓ ' : ''}
              {a.user_nombre ?? 'Alguien'}: {resumirAporte(a).slice(0, 80)}
            </option>
          ))}
        </select>
        <div className="flex items-start gap-2">
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={2}
            placeholder="La respuesta del «sistema»…"
            aria-label="Respuesta"
            maxLength={500}
            className="min-h-touch w-full rounded-md border border-border-strong bg-surface-sunken px-3 py-2 font-sans text-sm text-content placeholder:text-content-muted focus-visible:outline-none focus-visible:shadow-focus"
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={!listo}
            loading={mut.isPending}
            onClick={() => mut.mutate()}
            leadingIcon={<Send className="h-4 w-4" aria-hidden="true" />}
          >
            Responder
          </Button>
        </div>
      </div>

      {error && (
        <p className="mt-2 flex items-center gap-1.5 font-sans text-sm text-error">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </section>
  );
}
